import { LucideIcon } from "lucide-react";
import { AnimateOnScroll } from "@/components/AnimateOnScroll";

interface ImpactItem {
  title: string;
  description: string;
  icon: LucideIcon;
  stat: string;
  statLabel: string;
}

interface BusinessImpactSectionProps {
  badge: {
    icon: LucideIcon;
    text: string;
  };
  title: string;
  titleHighlight: string;
  subtitle: string;
  items: ImpactItem[];
  accentColor?: string;
}

export const BusinessImpactSection = ({
  badge,
  title,
  titleHighlight,
  subtitle,
  items,
  accentColor = "emerald",
}: BusinessImpactSectionProps) => {
  const BadgeIcon = badge.icon;

  const colorClasses: Record<string, { badge: string; title: string; icon: string; stat: string; border: string }> = {
    emerald: {
      badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      title: "text-emerald-600 dark:text-emerald-400",
      icon: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      stat: "text-emerald-600 dark:text-emerald-400",
      border: "hover:border-emerald-500/30",
    },
    teal: {
      badge: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
      title: "text-teal-600 dark:text-teal-400",
      icon: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
      stat: "text-teal-600 dark:text-teal-400",
      border: "hover:border-teal-500/30",
    },
    blue: {
      badge: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      title: "text-blue-600 dark:text-blue-400",
      icon: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      stat: "text-blue-600 dark:text-blue-400",
      border: "hover:border-blue-500/30",
    },
    sky: {
      badge: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
      title: "text-sky-600 dark:text-sky-400",
      icon: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
      stat: "text-sky-600 dark:text-sky-400",
      border: "hover:border-sky-500/30",
    },
  };

  const colors = colorClasses[accentColor] || colorClasses.emerald;

  return (
    <section className="py-12 md:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 ${colors.badge}`}>
              <BadgeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>{badge.text}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 px-2">
              {title} <span className={colors.title}>{titleHighlight}</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground px-2">{subtitle}</p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-5xl mx-auto">
          {items.map((item, index) => {
            const ItemIcon = item.icon;
            return (
              <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
                <div className={`text-center p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl bg-card border border-border transition-all duration-300 ${colors.border}`}>
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 ${colors.icon}`}>
                    <ItemIcon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" />
                  </div>
                  <div className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-0.5 sm:mb-1 ${colors.stat}`}>{item.stat}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 md:mb-4">{item.statLabel}</div>
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-foreground mb-1.5 sm:mb-2">{item.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{item.description}</p>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};
